import { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdSave, MdClose } from 'react-icons/md';
import PaymentRequestsCard, { PaymentRequest } from './PaymentRequestsCard';

// Buyer referral program: fixed commission per paid lead bought by the referees,
// on two levels (back: services/buyerAffiliateCommission.ts).

interface CommissionConfig {
    level1: number;
    level2: number;
}

interface RefereeRow {
    _id: string;
    name: string;
    email: string;
    createdAt: string;
    leadCount: number;
    totalDue: number;
}

interface BuyerReferrerRow {
    _id: string;
    name: string;
    email: string;
    affiliateCode: string;
    level1: RefereeRow[];
    level2: RefereeRow[];
    level1LeadCount: number;
    level2LeadCount: number;
    totalDue: number;
    credited: number;
    walletBalance: number;
}

interface RefereesModal {
    referrerName: string;
    level: 1 | 2;
    rows: RefereeRow[];
}

const fmt = (n: number) =>
    n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

function RefereesModalView({ modal, onClose }: { modal: RefereesModal; onClose: () => void }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-navy-800 shadow-xl flex flex-col max-h-[80vh]">
                <div className="flex items-center justify-between border-b border-gray-200 dark:border-navy-600 px-6 py-4">
                    <div>
                        <p className="text-base font-bold text-navy-700 dark:text-white">Filleuls acheteurs Niveau {modal.level}</p>
                        <p className="text-sm text-gray-500">{modal.referrerName}</p>
                    </div>
                    <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-700">
                        <MdClose className="text-xl" />
                    </button>
                </div>
                <div className="overflow-y-auto flex-1 px-6 py-4">
                    {modal.rows.length === 0 ? (
                        <div className="py-10 text-center text-gray-500">Aucun filleul.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-navy-600">
                                    {['Filleul', 'Inscrit le', 'Leads payés', 'Commission'].map((h) => (
                                        <th key={h} className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400 last:pr-0">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {[...modal.rows].sort((a, b) => b.leadCount - a.leadCount).map((row) => (
                                    <tr key={row._id} className="border-b border-gray-100 dark:border-navy-700">
                                        <td className="py-3 pr-4">
                                            <p className="font-semibold text-navy-700 dark:text-white">{row.name || '—'}</p>
                                            <p className="text-xs text-gray-500">{row.email}</p>
                                        </td>
                                        <td className="py-3 pr-4 text-xs text-gray-500">{new Date(row.createdAt).toLocaleDateString('fr-FR')}</td>
                                        <td className="py-3 pr-4 font-semibold text-navy-700 dark:text-white">{row.leadCount}</td>
                                        <td className="py-3 font-semibold text-navy-700 dark:text-white">{fmt(row.totalDue)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function BuyerSection({
    paymentRequests,
    onMarkedSent,
}: {
    paymentRequests: PaymentRequest[];
    onMarkedSent: (userId: string) => void;
}) {
    const [rows, setRows] = useState<BuyerReferrerRow[]>([]);
    const [config, setConfig] = useState<CommissionConfig>({ level1: 0, level2: 0 });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [modal, setModal] = useState<RefereesModal | null>(null);

    useEffect(() => {
        axios
            .get(`${CONFIG.apiUrl}/admin/buyer-affiliate-overview`, authHeaders())
            .then((res) => {
                setRows(res.data?.data ?? []);
                if (res.data?.config) setConfig(res.data.config);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaveError(null);
        setSaving(true);
        try {
            await axios.put(
                `${CONFIG.apiUrl}/admin/buyer-affiliate-config`,
                { level1: config.level1, level2: config.level2 },
                authHeaders()
            );
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch {
            setSaveError('Erreur lors de la sauvegarde.');
        } finally {
            setSaving(false);
        }
    };

    const totalLeads = rows.reduce((s, r) => s + r.level1LeadCount + r.level2LeadCount, 0);
    const totalDue = rows.reduce((s, r) => s + r.totalDue, 0);
    const totalCredited = rows.reduce((s, r) => s + r.credited, 0);

    return (
        <div className="flex flex-col gap-5">
            {modal && <RefereesModalView modal={modal} onClose={() => setModal(null)} />}

            {/* KPI row */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                {[
                    { label: 'Parrains acheteurs', value: loading ? '—' : String(rows.length) },
                    { label: 'Leads achetés', value: loading ? '—' : String(totalLeads) },
                    { label: 'Total dû', value: loading ? '—' : fmt(totalDue) },
                    { label: 'Déjà crédité', value: loading ? '—' : fmt(totalCredited) },
                ].map((kpi) => (
                    <Card key={kpi.label} extra="p-4">
                        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">{kpi.label}</p>
                        <p className="mt-1 text-2xl font-bold text-navy-700 dark:text-white">{kpi.value}</p>
                    </Card>
                ))}
            </div>

            {/* Commission config */}
            <Card extra="p-5">
                <div className="mb-5 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <p className="text-xl font-bold text-navy-700 dark:text-white">Commissions</p>
                        <p className="mt-1 text-sm text-gray-500">
                            Montant versé au parrain pour chaque lead payé acheté par ses filleuls acheteurs.
                        </p>
                    </div>
                    <button
                        onClick={handleSave}
                        disabled={saving || loading}
                        className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                    >
                        <MdSave className="text-lg" />
                        {saving ? 'Sauvegarde...' : saved ? 'Sauvegardé !' : 'Sauvegarder'}
                    </button>
                </div>

                {saveError && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{saveError}</div>}

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 max-w-lg">
                    {([
                        { key: 'level1' as const, label: 'Niveau 1 — filleul direct', desc: 'Par lead payé acheté par un acheteur directement parrainé.' },
                        { key: 'level2' as const, label: 'Niveau 2 — filleul indirect', desc: 'Par lead payé acheté par un acheteur parrainé par un filleul.' },
                    ] as const).map(({ key, label, desc }) => (
                        <div key={key} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <label className="block text-xs font-bold uppercase tracking-wide text-gray-600 mb-1">{label}</label>
                            <p className="mb-3 text-xs text-gray-500">{desc}</p>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={config[key]}
                                onChange={(e) => setConfig((c) => ({ ...c, [key]: parseFloat(e.target.value) || 0 }))}
                                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                            />
                        </div>
                    ))}
                </div>
            </Card>

            <PaymentRequestsCard requests={paymentRequests} onMarkedSent={onMarkedSent} />

            {/* Referrers table */}
            <Card extra="p-5">
                <p className="mb-5 text-xl font-bold text-navy-700 dark:text-white">Parrains acheteurs</p>
                {loading ? (
                    <div className="py-10 text-center text-gray-500">Chargement...</div>
                ) : rows.length === 0 ? (
                    <div className="py-10 text-center text-gray-500">Aucun parrain acheteur pour le moment.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-navy-600">
                                    {['Parrain', 'Code', 'N1 filleuls', 'N2 filleuls', 'Leads N1', 'Leads N2', 'Total dû', 'Crédité wallet', 'Solde wallet'].map((h) => (
                                        <th key={h} className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400 last:pr-0">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row._id} className="border-b border-gray-100 dark:border-navy-700">
                                        <td className="py-3 pr-4">
                                            <p className="font-semibold text-navy-700 dark:text-white">{row.name || '—'}</p>
                                            <p className="text-xs text-gray-500">{row.email}</p>
                                        </td>
                                        <td className="py-3 pr-4 font-mono text-xs font-semibold text-brand-500">{row.affiliateCode}</td>
                                        {([1, 2] as const).map((level) => {
                                            const list = level === 1 ? row.level1 : row.level2;
                                            return (
                                                <td key={level} className="py-3 pr-4">
                                                    {list.length > 0 ? (
                                                        <button
                                                            onClick={() => setModal({ referrerName: row.name || row.email, level, rows: list })}
                                                            className="font-semibold text-brand-500 hover:text-brand-600 underline underline-offset-2"
                                                        >
                                                            {list.length}
                                                        </button>
                                                    ) : (
                                                        <span className="text-gray-400">0</span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                        <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">{row.level1LeadCount}</td>
                                        <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">{row.level2LeadCount}</td>
                                        <td className="py-3 pr-4 font-semibold text-navy-700 dark:text-white">{fmt(row.totalDue)}</td>
                                        <td className="py-3 pr-4 font-semibold text-green-600">{fmt(row.credited)}</td>
                                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">{fmt(row.walletBalance)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}
